import { client } from "../api/client.js";
import { setPageContext } from "../utils/pageContext.js";
import { formatDateMDY, formatDateTimeMDY12h, toDatetimeLocalValue } from "../utils/date.js";
import { renderPageHeader } from "../ui/pageHeader.js";
import { renderMediaZone, bindMediaZoneEvents } from "../ui/mediaZone.js";
import { renderPreview } from "../ui/previews.js";
import { toast } from '../ui/toast.js';
import { confirmModal } from '../ui/confirmModal.js';
import { FailedPostsDrawer } from '../ui/failedPostsDrawer.js';
import { escapeHtml } from '../ui/escapeHtml.js';
import { openCampaignWizard } from '../ui/campaignWizard.js';
import { openMediaLibrary } from './create-social.js';
import { renderAiAssistToolbar, handleAiAssistClick } from '../ui/aiAssistToolbar.js';
import { renderCharCounter, updateCharCounter } from '../ui/charCounter.js';

export class CampaignsPage {
  constructor() {
    this.mode = "list"; // list | create | detail
    this.campaigns = [];
    this.currentCampaign = null;
    this.campaignItems = [];
    this.listSearch = '';
    this.listFilter = 'all';
    this.loading = false;
    this.error = null;
    this.form = {
      name: "",
      goal: "",
      context: "",
      platforms: ["facebook", "instagram", "x", "linkedin"],
      days: 7,
      totalItems: 12
    };

    // Phase 4B: Edit Drawer & Toasts
    this.editOpen = false;
    this.editItemId = null;
    this.editDraft = null;
    this.editOriginal = null;
    this.editDirty = false;
    this.editSaving = false;
    this.editError = null;
    this.toast = null;
    this.toastTimer = null;
    this.lastFocusedEl = null;
    this._boundEscHandler = this.handleEscKey.bind(this);
    this._boundBeforeUnload = this.handleBeforeUnload.bind(this);

    // Phase 4D: Power Tools State
    this.view = {
      search: "",
      filterPlatform: "all",
      filterStatus: "all",
      sort: "scheduled_asc", // scheduled_asc | scheduled_desc | platform_asc | status_asc | title_asc
      selectMode: false,
      mode: 'grid' // grid | list
    };
    this.selectedIds = new Set();
    this.bulkRunning = false;
    this.bulkProgress = 0;
    this.bulkTotal = 0;
  }

  async mount(container) {
    this.container = container;
    // Restore detail view if a campaign was previously open (survives page reload)
    const lastCampaignId = localStorage.getItem('campaigns:lastDetailId');
    if (lastCampaignId) {
      await this.loadDetail(lastCampaignId);
      // If detail load failed (stale ID from different user/session), fall back to list
      if (this.error) {
        this.error = null;
        await this.loadList();
      }
    } else {
      await this.loadList();
    }
  }

  async loadList() {
    if (this._failedDrawer) {
      this._failedDrawer.destroy();
      this._failedDrawer = null;
    }
    this.loading = true;
    this.mode = "list";
    localStorage.removeItem('campaigns:lastDetailId');
    this.render();

    try {
      const data = await client.getCampaigns();
      this.campaigns = data.campaigns || [];
      // Phase 31F: Load engagement rollup in parallel; degrade gracefully on failure.
      try {
        const bulk = await client.getCampaignsAnalyticsBulk();
        if (bulk?.ok) {
          const byId = {};
          (bulk.items || []).forEach(i => { byId[i.campaign_id] = i; });
          this.campaignAnalytics = byId;
        }
      } catch (_) { /* analytics is non-critical */ }
    } catch (err) {
      this.error = `Failed to load campaigns: ${err.message}`;
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async loadDetail(id) {
    if (this._failedDrawer) {
      this._failedDrawer.destroy();
      this._failedDrawer = null;
    }
    this.loading = true;
    this.mode = "detail";
    this.render();

    try {
      const data = await client.getCampaignDetail(id);
      this.currentCampaign = data.campaign;
      this.campaignItems = data.items || [];
      // Parse media_json for items loaded from API (DB stores JSON string)
      this.campaignItems.forEach(item => {
        if (!item.media?.length && item.media_json) {
          try { item.media = JSON.parse(item.media_json); } catch(_) { item.media = []; }
        }
        if (!item.media) item.media = [];
      });
      this.loadViewSettings();
      localStorage.setItem('campaigns:lastDetailId', id); // Only persist on success
      this.loading = false;
      this.render();
    } catch (err) {
      // Campaign not accessible (stale ID, different user, etc.) — fall back to list
      localStorage.removeItem('campaigns:lastDetailId');
      this.loading = false;
      await this.loadList();
    }
  }

  loadViewSettings() {
    if (!this.currentCampaign) return;
    const key = `campaign:${this.currentCampaign.id}:view`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        this.view = { ...this.view, ...parsed };
      } catch (e) {
        console.warn("Failed to parse view settings", e);
      }
    }
  }

  saveViewSettings() {
    if (!this.currentCampaign) return;
    const key = `campaign:${this.currentCampaign.id}:view`;
    localStorage.setItem(key, JSON.stringify(this.view));
  }

  render() {
    if (this.mode === "list") {
      this.renderList();
    } else if (this.mode === "create") {
      this.renderCreate();
    } else if (this.mode === "detail") {
      this.renderDetail();
    }

    this.bindEvents();
    this.mountFailedDrawer();
  }

  renderList() {
    const total = this.campaigns.length;
    const active = this.campaigns.filter(c => c.status === 'active').length;
    const draft = this.campaigns.filter(c => c.status === 'draft').length;
    const published = this.campaigns.reduce((sum, c) => sum + (c.published_count || 0), 0);

    this.container.innerHTML = `
      <div class="campaign-page" id="page-campaigns" data-testid="campaigns-page">
        ${renderPageHeader({
          eyebrow: "Campaign Management",
          title: "Campaigns",
          subtitle: "Track every operation from start to finish.",
          tourPlaylist: 'campaigns',
          rightHtml: `<button class="btn btn-primary" id="btn-show-create" data-action="new-campaign" data-testid="campaigns-create-btn">+ New Campaign</button>`
        })}

        <div class="campaign-list-container page-shell page-content">
          ${this.loading ? `
            <div class="loading-state" data-testid="campaigns-loading" style="padding: 24px;">
              <div class="skeleton skeleton-title" style="margin-bottom:16px;"></div>
              <div class="skeleton-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:16px;">
                <div class="skeleton skeleton-card"></div>
                <div class="skeleton skeleton-card"></div>
                <div class="skeleton skeleton-card"></div>
              </div>
            </div>
          ` : this.error ? `
            <div class="error-banner" style="margin-top: 20px;">${this.escape(this.error)}</div>
          ` : this.campaigns.length === 0 ? `
            <div class="empty-state">
              <div class="empty-state-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg></div>
              <div class="empty-state-title">No campaigns yet</div>
              <div class="empty-state-text">Campaigns group related content into trackable batches. The fastest path: ask Heist's chat to plan one for you.</div>
              <div class="empty-state-actions" style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;">
                <a href="#memory?tab=chat&prompt=Plan%20a%20product%20launch%20campaign%20for%20my%20brand%20over%20the%20next%203%20weeks" class="btn btn-primary" data-testid="empty-talk-to-heist">Talk to Heist &rarr;</a>
                <button class="btn btn-secondary" id="btn-show-create-empty" data-action="new-campaign" data-testid="campaigns-create-btn-empty">+ New Campaign</button>
              </div>
              <div class="campaign-templates">
                <p class="campaign-templates-label">Or start from a template</p>
                <div class="campaign-templates-grid">
                  <div class="campaign-template-card" data-action="use-template" data-template="launch">
                    <div class="campaign-template-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg></div>
                    <div class="campaign-template-info">
                      <div class="campaign-template-title">Product Launch</div>
                      <div class="campaign-template-desc">12 posts across 4 platforms over 3 weeks</div>
                    </div>
                    <button class="btn btn-sm btn-secondary" data-action="use-template" data-template="launch">Use Template &rarr;</button>
                  </div>
                  <div class="campaign-template-card" data-action="use-template" data-template="thought-leadership">
                    <div class="campaign-template-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2v.3h6v-.3c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z"/></svg></div>
                    <div class="campaign-template-info">
                      <div class="campaign-template-title">Weekly Thought Leadership</div>
                      <div class="campaign-template-desc">5 posts per week on LinkedIn + X, ongoing</div>
                    </div>
                    <button class="btn btn-sm btn-secondary" data-action="use-template" data-template="thought-leadership">Use Template &rarr;</button>
                  </div>
                  <div class="campaign-template-card" data-action="use-template" data-template="seasonal">
                    <div class="campaign-template-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg></div>
                    <div class="campaign-template-info">
                      <div class="campaign-template-title">Seasonal Content Series</div>
                      <div class="campaign-template-desc">20 posts themed around a season or holiday</div>
                    </div>
                    <button class="btn btn-sm btn-secondary" data-action="use-template" data-template="seasonal">Use Template &rarr;</button>
                  </div>
                </div>
              </div>
            </div>
          ` : `
            <div class="campaigns-stats" data-testid="campaigns-stats">
              <div class="kpi-card kpi-card--sm">
                <div class="kpi-card__label">Total</div>
                <div class="kpi-card__value">${total}</div>
              </div>
              <div class="kpi-card kpi-card--sm kpi-card--success">
                <div class="kpi-card__label">Active</div>
                <div class="kpi-card__value">${active}</div>
              </div>
              <div class="kpi-card kpi-card--sm kpi-card--warning">
                <div class="kpi-card__label">Draft</div>
                <div class="kpi-card__value">${draft}</div>
              </div>
              <div class="kpi-card kpi-card--sm kpi-card--info">
                <div class="kpi-card__label">Published</div>
                <div class="kpi-card__value">${published}</div>
              </div>
            </div>

            <div class="table-wrapper" data-testid="campaigns-grid">
              <div class="campaigns-table-header">
                <h3>All Campaigns</h3>
                <div class="table-controls">
                  <input type="text" id="campaign-list-search" placeholder="Search campaigns..." value="${this.escape(this.listSearch)}">
                  <select id="campaign-list-filter">
                    <option value="all"${this.listFilter === 'all' ? ' selected' : ''}>All Statuses</option>
                    <option value="active"${this.listFilter === 'active' ? ' selected' : ''}>Active</option>
                    <option value="draft"${this.listFilter === 'draft' ? ' selected' : ''}>Draft</option>
                  </select>
                </div>
              </div>
              <table class="data-table campaigns-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Platforms</th>
                    <th>Cadence</th>
                    <th>Progress</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody id="campaigns-tbody">
                  ${this._renderTableRows()}
                </tbody>
              </table>
            </div>
          `}
        </div>
      </div>
    `;
  }

  /* ── Campaign list table helpers ── */

  getFilteredCampaigns() {
    let list = this.campaigns;
    if (this.listFilter !== 'all') {
      list = list.filter(c => c.status === this.listFilter);
    }
    if (this.listSearch) {
      const q = this.listSearch.toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.goal || '').toLowerCase().includes(q)
      );
    }
    return list;
  }

  _renderTableRows() {
    const filtered = this.getFilteredCampaigns();
    if (filtered.length === 0) {
      return `<tr><td colspan="6" style="text-align:center;padding:32px;color:var(--text-muted)">No campaigns match your filters</td></tr>`;
    }
    return filtered.map(c => {
      const status = c.status || 'draft';
      const pct = c.item_count ? Math.round((c.published_count || 0) / c.item_count * 100) : 0;
      const engagement = (this.campaignAnalytics || {})[c.id];
      return `
        <tr data-id="${c.id}">
          <td class="campaign-name-cell">
            <span class="campaign-name-text">${this.escape(c.name)}</span>
            <small>${this.escape(c.goal || '')}</small>
            ${this._renderCampaignEngagementLine(engagement)}
          </td>
          <td><span class="status-dot status-dot--${status}">${status.toUpperCase()}</span></td>
          <td>${this._platformDotsHtml(c.platforms || [])}</td>
          <td>${this._formatCadence(c.cadence_days || [])}</td>
          <td>
            <div class="progress-cell">
              <div class="progress-bar-track"><div class="progress-bar-fill" style="width:${pct}%"></div></div>
              <span class="progress-pct">${pct}%</span>
            </div>
          </td>
          <td><button class="btn btn-secondary btn-sm btn-open" data-id="${c.id}">Open</button></td>
        </tr>`;
    }).join('');
  }

  renderCampaignTableBody() {
    const tbody = this.container.querySelector('#campaigns-tbody');
    if (tbody) tbody.innerHTML = this._renderTableRows();
  }

  // Phase 31F: Rendered under each campaign name — shows engagement stat row
  // from the bulk rollup endpoint. Falls back to a muted "No engagement data yet"
  // when a campaign has no published posts with analytics.
  _renderCampaignEngagementLine(rollup) {
    if (!rollup) {
      return `<div class="campaign-engagement-line campaign-engagement-line--empty" data-testid="campaign-engagement-line">0 posts · No engagement data yet</div>`;
    }
    const fmt = (n) => n >= 1000000 ? (n / 1000000).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(1) + 'K' : String(n);
    if (!rollup.post_count || rollup.post_count === 0) {
      return `<div class="campaign-engagement-line campaign-engagement-line--empty" data-testid="campaign-engagement-line">0 posts · No engagement data yet</div>`;
    }
    const rate = (rollup.avg_engagement_rate * 100).toFixed(1);
    return `<div class="campaign-engagement-line" data-testid="campaign-engagement-line">${rollup.post_count} posts · ${fmt(rollup.total_impressions)} impressions · ${rate}% avg engagement</div>`;
  }

  _platformDotsHtml(platforms) {
    if (!platforms.length) return '<span style="color:var(--text-muted)">—</span>';
    return `<div class="platform-dots">${platforms.map(p =>
      `<span class="platform-dot platform-dot--${p}" title="${p}"></span>`
    ).join('')}</div>`;
  }

  _formatCadence(days) {
    if (!days || days.length === 0) return '<span style="color:var(--text-muted)">—</span>';
    if (days.length >= 7) return 'Daily';
    return days.join(', ');
  }

  renderCreate() {
    this.container.innerHTML = `
        <div class="page-header">
          <button class="btn-link" id="btn-back-list">← Back to Campaigns</button>
          <h2>Create New Campaign</h2>
        </div>

        <form class="campaign-form" id="create-campaign-form">
          <div class="form-group">
            <label>Campaign Name</label>
            <input type="text" id="campaign-name" data-testid="campaign-name" required placeholder="e.g. Summer Product Launch" value="${this.escape(this.form.name)}">
          </div>
          
          <div class="form-group">
            <label>Campaign Goal / Topic</label>
            <textarea id="campaign-goal" data-testid="campaign-goal" required placeholder="What are we trying to achieve?">${this.escape(this.form.goal)}</textarea>
          </div>

          <div class="form-group">
            <label>Session Context (Optional)</label>
            <textarea id="campaign-context" data-testid="campaign-context" placeholder="Additional details for the AI...">${this.escape(this.form.context)}</textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Duration (Days)</label>
              <input type="number" id="campaign-days" data-testid="campaign-days" min="1" max="30" value="${this.form.days}">
            </div>
            <div class="form-group">
              <label>Total Items</label>
              <input type="number" id="campaign-total-items" data-testid="campaign-total-items" min="1" max="50" value="${this.form.totalItems}">
              <small id="cap-warning" class="hide">Max 50 items allowed.</small>
            </div>
          </div>

          <div class="form-group">
            <label>Posting Days</label>
            <div class="checkbox-group" style="flex-wrap:wrap; gap:8px;">
              ${['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(day => `
                <label class="checkbox-label">
                  <input type="checkbox" class="cadence-day-check" value="${day}" ${['Mon','Wed','Fri'].includes(day) ? 'checked' : ''}>
                  ${day}
                </label>
              `).join('')}
            </div>
          </div>
          <div class="form-group">
            <label>Posting Time</label>
            <input type="time" id="cadence-time" value="10:00" style="width:140px;">
          </div>

          <div class="form-group">
            <label>Platforms</label>
            <div class="checkbox-group">
              ${["facebook", "instagram", "x", "linkedin"].map(p => `
                <label class="checkbox-label">
                  <input type="checkbox" class="platform-check" value="${p}" data-testid="campaign-platform-${p}" ${this.form.platforms.includes(p) ? "checked" : ""}>
                  ${p.charAt(0).toUpperCase() + p.slice(1)}
                </label>
              `).join("")}
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" id="btn-cancel-create" data-testid="campaign-create-cancel">Cancel</button>
            <button type="submit" class="btn btn-primary" data-testid="campaign-create-submit">Create Campaign</button>
          </div>
        </form>

        <div id="loading-overlay" class="loading-overlay hide" data-testid="campaign-create-loading">
          <div class="loading-box">
            <div class="spinner"></div>
            <p>Generating up to ${this.form.totalItems} items... may take time in REAL mode.</p>
            <button class="btn btn-secondary btn-sm" id="btn-stop-waiting">Stop Waiting</button>
          </div>
        </div>
      `;
  }

  renderDetail() {
    const c = this.currentCampaign;

    // Always render with the data-testid expected by E2E tests
    this.container.innerHTML = `
      <div class="campaigns-page" id="page-campaigns" data-testid="campaign-detail">
        <div class="campaign-detail-topbar">
          <button class="btn btn-ghost btn-sm" id="btn-back-list">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
            Back to Campaigns
          </button>
          ${c ? `
          <div class="campaign-detail-actions">
            <a href="#calendar" class="btn btn-ghost btn-sm" data-testid="btn-view-calendar">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/></svg>
              View on Calendar
            </a>
            <button class="btn btn-glass btn-sm" id="btn-rewrite-campaign">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m16 12-4-4-4 4"/><path d="M12 16V8"/></svg>
              Update Campaign with Brain
            </button>
            <button class="btn btn-danger btn-sm" id="btn-delete-campaign" data-id="${c.id}" data-testid="btn-delete-campaign">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              Delete Campaign
            </button>
          </div>
          ` : ''}
        </div>
        <div class="campaign-detail-title">
          ${c ? `
            <h1>${this.escape(c.name)}</h1>
            <span class="status-badge status-${c.status}">${(c.status || 'draft').toUpperCase()}</span>
            <span id="campaign-failed-badge"></span>
          ` : `
            <h1>Campaign</h1>
          `}
        </div>

        <div class="campaign-detail-content">
          ${this.loading ? `
            <div class="loading-state" data-testid="campaign-detail-loading" style="padding: 40px; text-align: center;">Loading campaign details...</div>
          ` : this.error ? `
            <div class="error-banner" style="margin-top: 20px;">${this.escape(this.error)}</div>
          ` : !c ? `
            <p style="padding: 20px;">Campaign not found.</p>
          ` : `
            <div class="campaign-summary">
              <p><strong>Goal:</strong> ${this.escape(c.goal)}</p>
              ${this.renderCadenceSummary(c)}
            </div>
            
            <!-- Power Tools Toolbar -->
            <div class="campaign-toolbar">
              <div class="toolbar-left">
                <input type="text" id="campaign-search" data-testid="campaign-search" placeholder="Search items..." value="${this.escape(this.view.search)}">
                <select id="filter-platform" data-testid="campaign-filter-platform">
                  <option value="all">All Platforms</option>
                  ${[...new Set(this.campaignItems.map(i => i.platform))].filter(Boolean).map(p => `
                    <option value="${p}" ${this.view.filterPlatform === p ? 'selected' : ''}>${p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  `).join("")}
                </select>
                <select id="filter-status" data-testid="campaign-filter-status">
                  <option value="all">All Statuses</option>
                  ${[...new Set(this.campaignItems.map(i => i.status))].filter(Boolean).map(s => `
                    <option value="${s}" ${this.view.filterStatus === s ? 'selected' : ''}>${s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  `).join("")}
                </select>
                <select id="campaign-sort" data-testid="campaign-sort">
                  <option value="scheduled_asc" ${this.view.sort === 'scheduled_asc' ? 'selected' : ''}>Scheduled (Soonest)</option>
                  <option value="scheduled_desc" ${this.view.sort === 'scheduled_desc' ? 'selected' : ''}>Scheduled (Latest)</option>
                  <option value="platform_asc" ${this.view.sort === 'platform_asc' ? 'selected' : ''}>Platform (A→Z)</option>
                  <option value="status_asc" ${this.view.sort === 'status_asc' ? 'selected' : ''}>Status (A→Z)</option>
                  <option value="title_asc" ${this.view.sort === 'title_asc' ? 'selected' : ''}>Title (A→Z)</option>
                </select>
              </div>
              <div class="toolbar-right" style="display:flex;gap:8px;align-items:center;">
                 <div class="view-toggle">
                   <button class="view-toggle-btn ${this.view.mode === 'grid' ? 'active' : ''}" data-view-mode="grid" title="Grid view"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg></button>
                   <button class="view-toggle-btn ${this.view.mode === 'list' ? 'active' : ''}" data-view-mode="list" title="List view"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg></button>
                 </div>
                 <button class="btn btn-secondary ${this.view.selectMode ? 'active' : ''}" id="btn-toggle-select" data-testid="campaign-select-toggle">
                   ${this.view.selectMode ? 'Cancel Select' : 'Select Items'}
                 </button>
              </div>
            </div>

            ${this.view.selectMode ? `
              <div class="selection-bar" data-testid="selection-bar">
                <span class="selection-count">${this.selectedIds.size} selected</span>
                <div class="bulk-actions">
                  <select id="bulk-status" data-testid="campaign-bulk-status">
                    <option value="">Set Status...</option>
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="published">Published</option>
                  </select>
                  <button class="btn btn-secondary btn-sm" id="btn-bulk-status-apply" data-testid="campaign-bulk-status-apply">Apply</button>
                  <span class="bulk-divider">|</span>
                  <button class="btn btn-secondary btn-sm btn-shift" data-days="-1">-1 Day</button>
                  <button class="btn btn-secondary btn-sm btn-shift" data-days="1">+1 Day</button>
                  <button class="btn btn-secondary btn-sm btn-shift" data-days="7">+7 Days</button>
                  <input type="datetime-local" id="bulk-reschedule-date" class="bulk-reschedule-date" title="Set exact date/time for all selected">
                  <button class="btn btn-secondary btn-sm" id="btn-bulk-reschedule">Set Date</button>
                  <span class="bulk-divider">|</span>
                  <button class="btn btn-danger btn-sm" id="btn-bulk-clear-schedule">Clear Schedule</button>
                  <button class="btn btn-danger btn-sm" id="btn-bulk-delete">Delete</button>
                </div>
                ${this.bulkRunning ? `
                  <div class="bulk-progress" data-testid="campaign-bulk-progress">
                    Processing ${this.bulkProgress}/${this.bulkTotal}...
                  </div>
                ` : ''}
              </div>
            ` : ''}

            ${(() => {
        const filtered = this.getFilteredItems();
        if (filtered.length === 0) {
          return `<p style="padding: 20px;">No items match filters.</p>`;
        }
        if (this.view.mode === 'list') {
          return `<div class="campaign-items-list"><table class="campaign-items-table"><thead><tr>
            ${this.view.selectMode ? '<th style="width:32px"></th>' : ''}
            <th>Platform</th><th>Title</th><th>Status</th><th>Scheduled</th><th>Actions</th>
          </tr></thead><tbody>
          ${filtered.map(item => {
            const displayTitle = item.title || this.getComputedTitle(item);
            const isSelected = this.selectedIds.size > 0 && this.selectedIds.has(String(item.id));
            const schedVal = item.scheduled_for ? new Date(item.scheduled_for).toISOString().slice(0, 16) : '';
            const platMap = { facebook: { cls: 'pp-fb', label: 'Facebook' }, instagram: { cls: 'pp-ig', label: 'Instagram' }, x: { cls: 'pp-x', label: 'X' }, twitter: { cls: 'pp-x', label: 'X' }, linkedin: { cls: 'pp-li', label: 'LinkedIn' }, blog: { cls: 'pp-blog', label: 'WordPress' }, website: { cls: 'pp-website', label: 'Website' } };
            const plat = platMap[item.platform] || { cls: 'pp-fb', label: item.platform || 'Social' };
            const editingPlatform = this._editingCellId === String(item.id) && this._editingCellField === 'platform';
            const editingTitle = this._editingCellId === String(item.id) && this._editingCellField === 'content';
            return `<tr class="campaign-list-row ${isSelected ? 'selected' : ''}" data-item-id="${item.id}">
              ${this.view.selectMode ? `<td><input type="checkbox" class="item-checkbox" data-id="${item.id}" data-testid="campaign-item-checkbox" ${isSelected ? 'checked' : ''}></td>` : ''}
              <td class="ci-editable" data-ci-field="platform" data-ci-id="${item.id}">${editingPlatform
                ? `<select class="ll-inline-select ci-inline-edit" data-field="platform" data-ci-id="${item.id}">
                    <option value="facebook" ${item.platform === 'facebook' ? 'selected' : ''}>Facebook</option>
                    <option value="instagram" ${item.platform === 'instagram' ? 'selected' : ''}>Instagram</option>
                    <option value="x" ${item.platform === 'x' ? 'selected' : ''}>X</option>
                    <option value="linkedin" ${item.platform === 'linkedin' ? 'selected' : ''}>LinkedIn</option>
                    <option value="blog" ${item.platform === 'blog' ? 'selected' : ''}>Blog</option>
                  </select>`
                : `<span class="pp ${plat.cls}"><span class="pp-dot"></span>${plat.label}</span>`
              }</td>
              <td class="item-title-cell ci-editable" data-ci-field="content" data-ci-id="${item.id}">${editingTitle
                ? `<input class="ll-inline-input ci-inline-edit" data-field="content" data-ci-id="${item.id}" value="${this.escape(item.content || displayTitle)}" />`
                : this.escape(displayTitle)
              }</td>
              <td><select class="inline-status-select" data-item-id="${item.id}">
                <option value="draft"${item.status === 'draft' ? ' selected' : ''}>Draft</option>
                <option value="scheduled"${item.status === 'scheduled' ? ' selected' : ''}>Scheduled</option>
                <option value="published"${item.status === 'published' ? ' selected' : ''}>Published</option>
              </select></td>
              <td><input type="datetime-local" class="inline-schedule-input" data-item-id="${item.id}" value="${schedVal}" /></td>
              <td><button class="btn btn-ghost btn-sm" data-item-id="${item.id}" data-action="edit-item">Edit</button></td>
            </tr>`;
          }).join('')}
          </tbody></table></div>`;
        }
        return `<div class="campaign-items-grid" data-testid="campaign-items">
        ${filtered.map(item => {
          const displayTitle = item.title || this.getComputedTitle(item);
          const isSelected = this.selectedIds.size > 0 && this.selectedIds.has(String(item.id));
          return `
                    <div class="library-card campaign-item-card ${isSelected ? 'selected' : ''}"
                        data-testid="campaign-item"
                        data-item-id="${item.id}"
                        data-title="${this.escape(displayTitle)}"
                        data-status="${item.status || 'draft'}"
                        data-card-platform="${item.platform || ''}"
                        data-platform="${item.platform || ''}">
                      ${item.publish_status ? `<span class="camp-publish-dot camp-publish-dot--${this.escape(item.publish_status)}" title="Publish: ${this.escape(item.publish_status)}"></span>` : ''}
                      ${this.view.selectMode ? `
                        <div class="selection-overlay">
                          <input type="checkbox" class="item-checkbox"
                                 data-id="${item.id}"
                                 data-testid="campaign-item-checkbox"
                                 ${isSelected ? 'checked' : ''}>
                        </div>
                      ` : ''}
                      <div class="library-card-media library-card-preview campaign-item-preview">
                        ${renderPreview(item.platform || 'facebook', item.content || '', displayTitle, window.__auth__?.name || 'Your Brand', item.media)}
                      </div>
                      <div class="card-body" style="padding: 12px 14px;">
                        <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
                          <select class="card-inline-select card-inline-platform" data-card-field="platform" data-card-id="${item.id}" data-ci-field="platform" data-ci-id="${item.id}">
                            ${['facebook','instagram','x','linkedin','tiktok','youtube','pinterest','reddit'].map(p => `<option value="${p}" ${(item.platform || '') === p ? 'selected' : ''}>${p === 'x' ? 'X' : p.charAt(0).toUpperCase() + p.slice(1)}</option>`).join('')}
                          </select>
                          <select class="card-inline-select card-inline-status" data-card-field="status" data-card-id="${item.id}" data-ci-field="status" data-ci-id="${item.id}">
                            ${['draft','scheduled','published'].map(s => `<option value="${s}" ${(item.status || 'draft') === s ? 'selected' : ''}>${s.charAt(0).toUpperCase() + s.slice(1)}</option>`).join('')}
                          </select>
                        </div>
                        <input class="card-inline-title" data-card-field="title" data-card-id="${item.id}" data-ci-field="content" data-ci-id="${item.id}" value="${this.escape(displayTitle)}" />
                      </div>
                      <div class="library-card-footer" style="padding: 8px 14px;">
                        <div class="content-type-indicator">\u{1F4F1} Social · ${item.scheduled_for ? formatDateTimeMDY12h(item.scheduled_for) : formatDateMDY(item.created_at || '')}</div>
                        <div class="library-card-act-row">
                          <div class="perf-signal-btns">
                            <button class="perf-btn ${item.performance_signal === 'high' ? 'perf-btn--active-high' : ''}" data-perf-id="${item.id}" data-score="high" title="High performer">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                            </button>
                            <button class="perf-btn ${item.performance_signal === 'low' ? 'perf-btn--active-low' : ''}" data-perf-id="${item.id}" data-score="low" title="Low performer">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>
                            </button>
                          </div>
                          <button class="lib-act-btn" data-target-item-id="${item.id}" data-action="edit-item" title="Edit">
                            <svg viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button class="lib-act-btn lib-act-btn--danger" data-target-item-id="${item.id}" data-action="delete-item" title="Delete">
                            <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  `;
        }).join("")}
        </div>`;
      })()}

            <!-- Edit Drawer Overlay -->
            <div class="edit-drawer-overlay ${this.editOpen ? 'active' : ''}" id="edit-overlay"></div>

            <!-- Edit Drawer -->
            <div class="edit-drawer ${this.editOpen ? 'active' : ''}" data-testid="campaign-edit-drawer">
              <div class="edit-drawer-header">
                <h3>Edit Campaign Post</h3>
                <button class="btn-link" id="btn-close-drawer">Close</button>
              </div>
              <div class="drawer-body">
                <section class="drawer-editor">
                ${this.editError ? `<div class="edit-error-banner">${this.escape(this.editError)}</div>` : ''}

                <div class="edit-field">
                  <label>Title</label>
                  <div style="display:flex; gap:8px;">
                    <input type="text" id="edit-title" data-testid="campaign-edit-title"
                           value="${this.escape(this.editDraft?.title || '')}" placeholder="Post title...">
                    <button class="btn btn-secondary btn-sm" id="btn-title-autofill" data-testid="campaign-edit-title-autofill">Autofill</button>
                  </div>
                </div>

                <div class="edit-field">
                  <label for="edit-scheduled" style="cursor:pointer">Scheduled Date & Time</label>
                  <input type="datetime-local" id="edit-scheduled" data-testid="campaign-edit-scheduled"
                         value="${this.editDraft?.scheduled_for ? toDatetimeLocalValue(this.editDraft.scheduled_for) : ''}">
                  <span id="edit-scheduled-preview" class="edit-hint">
                    ${this.editDraft?.scheduled_for ? `Display: ${formatDateTimeMDY12h(this.editDraft.scheduled_for)}` : ''}
                  </span>
                </div>

                <div class="edit-field">
                  <label>Content</label>
                  ${renderAiAssistToolbar({ showFillAi: true, showFillToAll: false })}
                  <textarea id="edit-content" data-testid="campaign-edit-content">${this.escape(this.editDraft?.content || '')}</textarea>
                  ${renderCharCounter({ platform: this.editDraft?.platform, initialLength: (this.editDraft?.content || '').length, counterId: 'camp-char-counter' })}
                </div>

                <div class="edit-field">
                  ${renderMediaZone(this.editDraft?.media || [], 'campaign-media-input')}
                  ${window.__auth__?.workspace_id ? '<button class="media-library-btn" id="campaign-open-media-library">Browse Media Library</button>' : ''}
                </div>

                <div class="edit-field">
                  <label>Media URLs</label>
                  <div style="display:flex; gap:8px;">
                    <input type="text" id="edit-media-url" data-testid="campaign-edit-media-input" placeholder="Paste image URL..." style="flex:1;">
                    <button class="btn btn-secondary btn-sm" id="btn-add-media-url" data-testid="campaign-edit-media-add">Add</button>
                  </div>
                  <div id="media-url-list" style="margin-top:8px;">
                    ${(this.editDraft?.mediaUrls || []).map((url, i) => `
                      <div class="media-url-item" data-testid="campaign-edit-media-item" data-media-url-index="${i}" style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
                        <img src="${this.escape(url)}" alt="media" style="width:48px;height:48px;object-fit:cover;border-radius:4px;">
                        <span style="flex:1;font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${this.escape(url)}</span>
                        <button class="btn btn-ghost btn-sm btn-remove-media-url" data-remove-url-index="${i}">&times;</button>
                      </div>
                    `).join('')}
                  </div>
                </div>

                <div class="edit-field">
                  <button class="btn btn-ghost btn-sm" id="btn-copy-content">Copy</button>
                </div>
                </section>
                <section class="drawer-preview">
                  <div class="preview-fixed-header">
                    <span>PREVIEW</span>
                  </div>
                  <div class="pane-scroll-area preview-scroll">
                    <div class="preview-container" id="campaign-drawer-preview">
                      ${renderPreview(this.editDraft?.platform || 'facebook', this.editDraft?.content || '', this.editDraft?.title || '', window.__auth__?.name || 'Your Brand', this.editDraft?.media)}
                    </div>
                  </div>
                </section>
              </div>
              <div class="edit-drawer-footer">
                <span id="save-indicator" class="saving-indicator ${this.editDirty || !this.editOpen ? 'hide' : ''}" data-testid="campaign-save-indicator">Saved &#10003;</span>
                ${this.editSaving ? '<span class="saving-indicator">Saving...</span>' : ''}
                <button class="btn btn-secondary" id="btn-edit-cancel" data-testid="campaign-edit-cancel">Cancel</button>
                <button class="btn btn-primary" id="btn-edit-save" data-testid="campaign-edit-save" ${!this.editDirty || this.editSaving ? 'disabled' : ''}>Save Changes</button>
              </div>
            </div>
          `}
        </div>

        <!-- Toast Notifications -->
        <div class="toast-container" data-testid="toast-container">
          ${this.toast ? `
            <div class="toast toast-${this.toast.type}" data-testid="toast">
              <span class="toast-message">${this.escape(this.toast.message)}</span>
            </div>
          ` : ""}
        </div>
      </div>
    `;
  }


  bindEvents() {
    // Robust event delegation on this.container for primary navigation and button actions
    // this.container is stable across renders. Using onclick ensures idempotency.
    this.container.onclick = (e) => {
      const target = e.target;

      // Campaign template — open wizard with pre-fill
      const templateBtn = target.closest("[data-action='use-template']");
      if (templateBtn) {
        const TEMPLATES = {
          'launch': { name: 'Product Launch', goal: 'Build awareness and drive conversions for a new product launch across all platforms', context: 'Focus on teaser content, feature highlights, social proof, and clear CTAs. Build anticipation in week 1, showcase features in week 2, drive action in week 3.', totalItems: 12, durationDays: 21, selectedDays: ['Mon', 'Wed', 'Fri'] },
          'thought-leadership': { name: 'Weekly Thought Leadership', goal: 'Establish authority and grow professional audience through consistent expert insights', context: 'Share industry insights, lessons learned, data-backed opinions, and actionable tips. Mix formats: short takes, threads, and longer reflections.', totalItems: 10, durationDays: 14, selectedDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] },
          'seasonal': { name: 'Seasonal Content Series', goal: 'Create a themed content series around an upcoming season or holiday', context: 'Blend seasonal themes with brand messaging. Include holiday tips, seasonal offers, behind-the-scenes content, and audience engagement posts.', totalItems: 20, durationDays: 28, selectedDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] }
        };
        var tpl = TEMPLATES[templateBtn.dataset.template];
        if (tpl) {
          openCampaignWizard({
            template: tpl,
            onComplete: (id) => {
              if (id) this.loadDetail(id);
              else this.loadCampaigns();
            }
          });
        }
        return;
      }

      // New Campaign — open wizard
      const createBtn = target.closest("[data-action='new-campaign']");
      if (createBtn) {
        openCampaignWizard({
          onComplete: (id) => {
            if (id) this.loadDetail(id);
            else this.loadCampaigns();
          }
        });
        return;
      }

      // Open Campaign
      const openBtn = target.closest(".btn-open");
      if (openBtn) {
        this.loadDetail(openBtn.dataset.id);
        return;
      }

      // Back to List / Cancel
      const backBtn = target.closest("#btn-back-list") || target.closest("#btn-cancel-create") || target.closest("#btn-err-back") || target.closest("#btn-cancel-edit");
      if (backBtn) {
        if (backBtn.id === "btn-cancel-edit") {
          this.closeEditDrawer();
        } else {
          this.loadList();
        }
        return;
      }
    };

    // Campaign list search & filter
    const listSearch = this.container.querySelector('#campaign-list-search');
    if (listSearch) {
      listSearch.addEventListener('input', () => {
        this.listSearch = listSearch.value;
        this.renderCampaignTableBody();
      });
    }
    const listFilter = this.container.querySelector('#campaign-list-filter');
    if (listFilter) {
      listFilter.addEventListener('change', () => {
        this.listFilter = listFilter.value;
        this.renderCampaignTableBody();
      });
    }

    // List events (specific ones not covered by delegation)
    this.container.querySelectorAll(".btn-delete").forEach(btn => {
      btn.addEventListener("click", async () => {
        if (await confirmModal({ title: 'Delete Campaign', message: 'Delete this campaign and all its generated content?', confirmLabel: 'Delete', danger: true })) {
          try {
            await client.deleteCampaign(btn.dataset.id);
            await this.loadList();
          } catch (err) {
            toast('error', "Delete failed: " + err.message);
          }
        }
      });
    });

    const formEl = this.container.querySelector("#create-campaign-form");
    if (formEl) {
      formEl.addEventListener("submit", async (e) => {
        e.preventDefault();

        const payload = {
          name: this.container.querySelector("#campaign-name").value,
          goal: this.container.querySelector("#campaign-goal").value,
          context: this.container.querySelector("#campaign-context").value,
          platforms: Array.from(this.container.querySelectorAll(".platform-check:checked")).map(cb => cb.value),
          cadence: {
            days: parseInt(this.container.querySelector("#campaign-days").value),
            totalItems: parseInt(this.container.querySelector("#campaign-total-items").value)
          }
        };

        if (payload.cadence.totalItems > 50) {
          toast('warning', "Max 50 items allowed.");
          return;
        }

        const checkedDays = Array.from(this.container.querySelectorAll('.cadence-day-check:checked')).map(cb => cb.value);
        const cadenceTime = this.container.querySelector('#cadence-time')?.value || '10:00';
        const cadenceRules = [];
        for (const platform of payload.platforms) {
          for (const day of checkedDays) {
            cadenceRules.push({ day, platform, time: cadenceTime });
          }
        }
        payload.cadenceRules = cadenceRules;

        this.container.querySelector("#loading-overlay").classList.remove("hide");

        try {
          const result = await client.createCampaign(payload);
          await this.loadDetail(result.campaignId);
        } catch (err) {
          this.container.querySelector("#loading-overlay")?.classList.add("hide");
          this.error = `Create failed: ${err.message} `;
          this.render();
        }
      });

      this.container.querySelector("#campaign-total-items")?.addEventListener("input", (e) => {
        const val = parseInt(e.target.value);
        const warning = this.container.querySelector("#cap-warning");
        if (val > 50) warning.classList.remove("hide");
        else warning.classList.add("hide");
      });
    }

    this.container.querySelector("#btn-stop-waiting")?.addEventListener("click", () => {
      this.loadList();
    });

    // Detail events: Item Click to Edit or Select (Grid view)
    this.container.querySelectorAll(".campaign-item-card").forEach(card => {
      card.addEventListener("click", (e) => {
        // If clicking checkbox specifically, handled by change listener
        if (e.target.closest(".selection-overlay")) return;
        // Let inline edit fields handle their own clicks, don't open drawer
        if (e.target.closest('.card-inline-select') ||
            e.target.closest('.card-inline-title') ||
            e.target.closest('.card-inline-platform') ||
            e.target.tagName === 'SELECT' ||
            e.target.tagName === 'INPUT' ||
            e.target.tagName === 'OPTION') {
          return;
        }

        const itemId = card.dataset.itemId;

        if (this.view.selectMode) {
          if (this.selectedIds.has(itemId)) this.selectedIds.delete(itemId);
          else this.selectedIds.add(itemId);
          this.render();
        } else {
          const item = this.campaignItems.find(i => String(i.id) === String(itemId));
          if (item) this.openEditDrawer(item, card);
        }
      });
    });

    // Inline card select save handlers (platform, status selects on grid cards)
    this.container.querySelectorAll('.card-inline-select').forEach(sel => {
      sel.addEventListener('change', async (e) => {
        e.stopPropagation();
        const itemId = sel.dataset.cardId || sel.dataset.ciId;
        const field = sel.dataset.cardField || sel.dataset.ciField;
        const value = sel.value;
        try {
          await client.updateCampaignItem(this.editCampaignId || this.selectedCampaignId || this.currentCampaign?.id, itemId, { [field]: value });
          const item = this.campaignItems?.find(i => String(i.id) === String(itemId));
          if (item) item[field] = value;
          sel.classList.add('inline-save-flash');
          setTimeout(() => sel.classList.remove('inline-save-flash'), 600);
        } catch(err) { console.error('Inline save failed:', err); toast('error', 'Couldn\'t save that change — please try again.'); }
      });
    });

    // Inline card title save handlers (title input on grid cards)
    this.container.querySelectorAll('.card-inline-title').forEach(inp => {
      const save = async () => {
        const itemId = inp.dataset.cardId || inp.dataset.ciId;
        const value = inp.value.trim();
        if (!value) return;
        try {
          await client.updateCampaignItem(this.editCampaignId || this.selectedCampaignId || this.currentCampaign?.id, itemId, { title: value });
          const item = this.campaignItems?.find(i => String(i.id) === String(itemId));
          if (item) item.title = value;
          inp.classList.add('inline-save-flash');
          setTimeout(() => inp.classList.remove('inline-save-flash'), 600);
        } catch(err) { console.error('Inline save failed:', err); toast('error', 'Couldn\'t save that change — please try again.'); }
      };
      inp.addEventListener('blur', save);
      inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); inp.blur(); } });
      inp.addEventListener('click', (e) => e.stopPropagation());
    });

    // Detail events: Edit button in list view
    this.container.querySelectorAll('[data-action="edit-item"]').forEach(btn => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        // Grid-layout buttons use data-target-item-id to avoid colliding with
        // the card-level data-item-id; list-layout still uses data-item-id.
        const itemId = btn.dataset.targetItemId || btn.dataset.itemId;
        const item = this.campaignItems.find(i => String(i.id) === String(itemId));
        if (item) this.openEditDrawer(item, btn);
      });
    });

    // Detail events: Delete button per campaign item (soft-delete)
    this.container.querySelectorAll('[data-action="delete-item"]').forEach(btn => {
      btn.addEventListener("click", async (e) => {
        e.stopPropagation();
        const itemId = btn.dataset.itemId;
        if (!itemId) return;
        const ok = await confirmModal({
          title: 'Delete Post',
          message: 'Delete this post from the campaign? It will be moved to Trash for 30 days.',
          confirmLabel: 'Delete',
          danger: true
        });
        if (!ok) return;
        try {
          await client.deleteFromLibrary(itemId);
          this.campaignItems = this.campaignItems.filter(i => String(i.id) !== String(itemId));
          this.selectedIds.delete(itemId);
          this.renderDetail();
          this.showToast("success", "Post deleted.");
        } catch (err) {
          console.error('Delete campaign item failed:', err);
          this.showToast("error", "Delete failed: " + (err.message || 'unknown error'));
        }
      });
    });

    // Detail events: Delete entire campaign
    this.container.querySelector('#btn-delete-campaign')?.addEventListener("click", async (e) => {
      e.stopPropagation();
      const btn = e.currentTarget;
      const campaignId = btn.dataset.id;
      const campaignName = this.currentCampaign?.name || 'this campaign';
      const itemCount = (this.campaignItems || []).length;
      const ok = await confirmModal({
        title: 'Delete Campaign',
        message: `Delete "${campaignName}" and all ${itemCount} of its posts? This cannot be undone — posts will be removed from the Library, Calendar, and any pending schedules.`,
        confirmLabel: 'Delete Campaign',
        danger: true
      });
      if (!ok) return;
      try {
        btn.disabled = true;
        await client.deleteCampaign(campaignId);
        this.currentCampaign = null;
        this.campaignItems = [];
        this.selectedIds.clear();
        this.view = 'list';
        await this.loadList();
        this.render();
        this.showToast("success", `Campaign "${campaignName}" deleted.`);
      } catch (err) {
        console.error('Delete campaign failed:', err);
        btn.disabled = false;
        this.showToast("error", "Delete failed: " + (err.message || 'unknown error'));
      }
    });

    // Inline status change in list view
    this.container.querySelectorAll('.inline-status-select').forEach(sel => {
      sel.addEventListener("change", async (e) => {
        e.stopPropagation();
        const itemId = sel.dataset.itemId;
        const newStatus = sel.value;
        try {
          const result = await client.updateCampaignItem(this.currentCampaign.id, itemId, { status: newStatus });
          this.campaignItems = this.campaignItems.map(i => String(i.id) === String(itemId) ? result.data.item : i);
          sel.classList.add('inline-save-flash');
          setTimeout(() => sel.classList.remove('inline-save-flash'), 600);
        } catch (err) {
          console.error('Inline status update failed:', err);
          sel.value = this.campaignItems.find(i => String(i.id) === String(itemId))?.status || 'draft';
        }
      });
    });

    // Inline schedule change in list view
    this.container.querySelectorAll('.inline-schedule-input').forEach(inp => {
      inp.addEventListener("change", async (e) => {
        e.stopPropagation();
        const itemId = inp.dataset.itemId;
        const newDate = inp.value ? new Date(inp.value).toISOString() : null;
        try {
          const result = await client.updateCampaignItem(this.currentCampaign.id, itemId, { scheduled_for: newDate });
          this.campaignItems = this.campaignItems.map(i => String(i.id) === String(itemId) ? result.data.item : i);
          inp.classList.add('inline-save-flash');
          setTimeout(() => inp.classList.remove('inline-save-flash'), 600);
        } catch (err) {
          console.error('Inline schedule update failed:', err);
          toast('error', 'Couldn\'t reschedule that post — please try again.');
        }
      });
    });

    // Seamless inline cell editing — click platform/content cell to edit
    this.container.querySelectorAll('.ci-editable').forEach(cell => {
      cell.addEventListener('click', (e) => {
        if (e.target.closest('.ci-inline-edit')) return; // already editing
        e.stopPropagation();
        this._editingCellId = cell.dataset.ciId;
        this._editingCellField = cell.dataset.ciField;
        this.render();
        var focusEl = this.container.querySelector('.ci-inline-edit');
        if (focusEl) { focusEl.focus(); if (focusEl.select) focusEl.select(); }
      });
    });

    // Auto-save on blur/change for inline cell edits
    this.container.querySelectorAll('.ci-inline-edit').forEach(el => {
      var commitFn = async () => {
        var itemId = el.dataset.ciId;
        var field = el.dataset.field;
        var value = el.value;
        var item = this.campaignItems.find(i => String(i.id) === String(itemId));
        if (item && item[field] !== value) {
          try {
            var result = await client.updateCampaignItem(this.currentCampaign.id, itemId, { [field]: value });
            this.campaignItems = this.campaignItems.map(i => String(i.id) === String(itemId) ? result.data.item : i);
          } catch (err) { console.error('Inline edit failed:', err); toast('error', 'Couldn\'t save that edit — please try again.'); }
        }
        this._editingCellId = null;
        this._editingCellField = null;
        this.render();
      };
      if (el.tagName === 'SELECT') {
        el.addEventListener('change', commitFn);
        el.addEventListener('blur', () => { this._editingCellId = null; this._editingCellField = null; this.render(); });
      } else {
        el.addEventListener('blur', commitFn);
        el.addEventListener('keydown', (e2) => {
          if (e2.key === 'Enter') { e2.preventDefault(); el.blur(); }
          if (e2.key === 'Escape') { this._editingCellId = null; this._editingCellField = null; this.render(); }
        });
      }
    });

    // List row click to select (when in select mode)
    this.container.querySelectorAll('.campaign-list-row').forEach(row => {
      row.addEventListener("click", (e) => {
        if (e.target.closest('[data-action]') || e.target.closest('.inline-status-select') || e.target.closest('.inline-schedule-input') || e.target.closest('.item-checkbox') || e.target.closest('.ci-editable')) return;
        if (!this.view.selectMode) return;
        const itemId = row.dataset.itemId;
        if (this.selectedIds.has(itemId)) this.selectedIds.delete(itemId);
        else this.selectedIds.add(itemId);
        this.render();
      });
    });

    this.container.querySelectorAll(".item-checkbox").forEach(cb => {
      cb.addEventListener("change", (e) => {
        const itemId = cb.dataset.id;
        if (e.target.checked) this.selectedIds.add(itemId);
        else this.selectedIds.delete(itemId);
        this.render();
      });
    });

    // Phase 4D: Toolbar Events
    const searchInput = this.container.querySelector("#campaign-search");
    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        this.view.search = e.target.value.toLowerCase();
        this.saveViewSettings();
        this.render();
        // Maintain focus
        this.container.querySelector("#campaign-search")?.focus();
      });
    }

    ["filter-platform", "filter-status", "campaign-sort"].forEach(id => {
      this.container.querySelector(`#${id} `)?.addEventListener("change", (e) => {
        const key = id === "filter-platform" ? "filterPlatform" : (id === "filter-status" ? "filterStatus" : "sort");
        this.view[key] = e.target.value;
        this.saveViewSettings();
        this.render();
      });
    });

    this.container.querySelector("#btn-toggle-select")?.addEventListener("click", () => {
      this.view.selectMode = !this.view.selectMode;
      if (!this.view.selectMode) this.selectedIds.clear();
      this.saveViewSettings();
      this.render();
    });

    // View mode toggle (grid/list)
    this.container.querySelectorAll('[data-view-mode]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.view.mode = btn.dataset.viewMode;
        this.saveViewSettings();
        this.render();
      });
    });

    this.container.querySelector("#btn-rewrite-campaign")?.addEventListener("click", () => {
      this.handleRewriteCampaign();
    });

    // Scheduled Input Preview
    const scheduledInput = this.container.querySelector("#edit-scheduled");
    if (scheduledInput) {
      scheduledInput.addEventListener("input", (e) => {
        const val = e.target.value;
        const previewEl = this.container.querySelector("#edit-scheduled-preview");
        if (previewEl) {
          previewEl.textContent = val ? `Display: ${formatDateTimeMDY12h(val)} ` : "";
        }
      });
    }

    // Bulk Actions
    this.container.querySelector("#btn-bulk-status-apply")?.addEventListener("click", () => {
      const status = this.container.querySelector("#bulk-status").value;
      if (status) this.runBulkAction({ status });
    });

    this.container.querySelectorAll(".btn-shift").forEach(btn => {
      btn.addEventListener("click", () => {
        const days = parseInt(btn.dataset.days);
        this.runBulkAction({ shiftDays: days });
      });
    });

    this.container.querySelector("#btn-bulk-clear-schedule")?.addEventListener("click", async () => {
      if (await confirmModal({ title: 'Clear Schedule', message: `Clear schedule for ${this.selectedIds.size} items?`, confirmLabel: 'Clear', danger: false })) {
        this.runBulkAction({ scheduled_for: null });
      }
    });

    // BF-7: Bulk reschedule to a specific date/time
    this.container.querySelector("#btn-bulk-reschedule")?.addEventListener("click", async () => {
      const dateInput = this.container.querySelector("#bulk-reschedule-date");
      const value = dateInput?.value;
      if (!value) {
        this.showToast("error", "Pick a date/time first.");
        return;
      }
      if (await confirmModal({ title: 'Set Schedule', message: `Set ${this.selectedIds.size} items to ${new Date(value).toLocaleString()}?`, confirmLabel: 'Set Date', danger: false })) {
        this.runBulkAction({ scheduled_for: new Date(value).toISOString() });
      }
    });

    // BF-7: Bulk delete
    this.container.querySelector("#btn-bulk-delete")?.addEventListener("click", async () => {
      if (this.selectedIds.size === 0) return;
      if (await confirmModal({ title: 'Delete Items', message: `Delete ${this.selectedIds.size} campaign items? They will be moved to Trash.`, confirmLabel: 'Delete', danger: true })) {
        await this.runBulkDelete();
      }
    });

    // Drawer events
    this.container.querySelector("#edit-overlay")?.addEventListener("click", () => this.closeEditDrawer());
    this.container.querySelector("#btn-close-drawer")?.addEventListener("click", () => this.closeEditDrawer());
    this.container.querySelector("#btn-edit-cancel")?.addEventListener("click", () => this.closeEditDrawer());

    this.container.querySelector("#btn-edit-save")?.addEventListener("click", () => this.saveEdit());

    // AI Assist toolbar in edit drawer
    const editDrawer = this.container.querySelector('[data-testid="campaign-edit-drawer"]');
    if (editDrawer) {
      editDrawer.addEventListener('click', async (e) => {
        const handled = await handleAiAssistClick(
          e,
          () => this.container.querySelector('#edit-content')?.value || '',
          (text) => {
            const el = this.container.querySelector('#edit-content');
            if (el) el.value = text;
            if (this.editDraft) { this.editDraft.content = text; this.editDirty = true; }
            // Live-update preview after AI change
            this._updateCampaignPreview();
          },
          this.editDraft?.platform || 'general',
          (msg, kind) => toast(kind, msg)
        );
      });
    }

    // Phase 4C: Title Autofill
    this.container.querySelector("#btn-title-autofill")?.addEventListener("click", async () => {
      const suggested = this.getComputedTitle(this.editDraft);
      if (this.editDraft.title && this.editDraft.title.trim() !== "") {
        if (!await confirmModal({ title: 'Replace Title', message: 'Replace existing title?', confirmLabel: 'Replace', danger: false })) return;
      }
      this.editDraft.title = suggested;
      this.editDirty = true;
      this.render();
      // Refocus title
      this.container.querySelector("#edit-title")?.focus();
    });

    // Phase 4C: Character Counter & Dirty tracking
    const editTitle = this.container.querySelector("#edit-title");
    if (editTitle) {
      editTitle.addEventListener("input", (e) => {
        this.editDraft.title = e.target.value;
        this.checkDirty();
        this._updateCampaignPreview();
      });
    }

    const editContext = this.container.querySelector("#edit-context");
    if (editContext) {
      editContext.addEventListener("input", (e) => {
        this.editDraft.context = e.target.value;
        this.checkDirty();
      });
    }

    const editScheduled = this.container.querySelector("#edit-scheduled");
    if (editScheduled) {
      editScheduled.addEventListener("change", (e) => {
        this.editDraft.scheduled_for = e.target.value ? new Date(e.target.value).toISOString() : null;
        this.checkDirty();
      });
    }

    const editContent = this.container.querySelector("#edit-content");
    if (editContent) {
      editContent.addEventListener("input", (e) => {
        const newVal = e.target.value;
        this.editDraft.content = newVal;
        this.checkDirty();
        this._updateCampaignPreview();

        // Update char count inline if possible
        const counter = this.container.querySelector(".char-count");
        if (counter) {
          counter.innerText = `${newVal.length} characters`;
          if (this.editDraft.platform === 'x' && newVal.length > 280) counter.classList.add("warning");
          else counter.classList.remove("warning");
        }
      });
    }

    // Media zone (drag-drop file attachment) — only when edit drawer is open
    if (this.editDraft) {
      if (!this.editDraft.media) this.editDraft.media = [];
      bindMediaZoneEvents(this.container, this.editDraft.media, (updated) => {
        this.editDraft.media = updated;
        this.editDirty = true;
        this.render();
      }, 'campaign-media-input');

      // Browse Media Library button
      const mlBtn = this.container.querySelector('#campaign-open-media-library');
      if (mlBtn) mlBtn.onclick = () => openMediaLibrary(this);

      // Media URL add button
      if (!this.editDraft.mediaUrls) this.editDraft.mediaUrls = [];
      const btnAddMediaUrl = this.container.querySelector('#btn-add-media-url');
      if (btnAddMediaUrl) {
        btnAddMediaUrl.addEventListener('click', () => {
          const input = this.container.querySelector('#edit-media-url');
          const url = input?.value?.trim();
          if (url) {
            this.editDraft.mediaUrls.push(url);
            this.editDirty = true;
            this.render();
          }
        });
      }
      // Media URL remove buttons
      this.container.querySelectorAll('.btn-remove-media-url').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const idx = parseInt(btn.dataset.removeUrlIndex);
          this.editDraft.mediaUrls.splice(idx, 1);
          this.editDirty = true;
          this.render();
        });
      });
    }

    // Copy Content
    this.container.querySelector("#btn-copy-content")?.addEventListener("click", () => {
      const text = this.editDraft.content;
      navigator.clipboard.writeText(text).then(() => {
        const btn = this.container.querySelector("#btn-copy-content");
        btn.innerText = "Copied!";
        setTimeout(() => { if (btn) btn.innerText = "Copy"; }, 2000);
      });
    });

    // Toast events
    this.container.querySelector(".toast-close")?.addEventListener("click", () => this.clearToast());
  }

  openEditDrawer(item, el) {
    this.lastFocusedEl = el;
    this.editOpen = true;
    this.editItemId = item.id;
    this.editOriginal = { ...item };
    const parsedMedia = Array.isArray(item.media) && item.media.length > 0
      ? [...item.media]
      : (item.media_json ? (() => { try { return JSON.parse(item.media_json); } catch(_) { return []; } })() : []);
    this.editDraft = { ...item, media: parsedMedia };
    this.editDirty = false;
    this.editSaving = false;
    this.editError = null;

    this.render();

    // Auto focus
    setTimeout(() => {
      this.container.querySelector("#edit-content")?.focus();
    }, 50);

    // Bind Esc and Nav guards
    window.addEventListener("keydown", this._boundEscHandler);
    window.addEventListener("beforeunload", this._boundBeforeUnload);
  }

  async closeEditDrawer(force = false) {
    if (!force && this.editDirty) {
      if (!await confirmModal({ title: 'Discard Changes', message: 'Discard unsaved changes?', confirmLabel: 'Discard', danger: false })) return;
    }

    this.editOpen = false;
    this.editDirty = false;

    // Surgical close: hide drawer/overlay without re-sorting the items grid
    const drawer = this.container.querySelector('.edit-drawer');
    const overlay = this.container.querySelector('.edit-drawer-overlay');
    if (drawer) drawer.classList.remove('active');
    if (overlay) overlay.classList.remove('active');

    // Update the edited item card's data attributes in-place (no re-sort)
    if (this.editItemId) {
      const editedItem = this.campaignItems.find(i => String(i.id) === String(this.editItemId));
      const card = this.container.querySelector(`[data-item-id="${this.editItemId}"]`);
      if (card && editedItem) {
        const displayTitle = editedItem.title || this.getComputedTitle(editedItem);
        card.setAttribute('data-title', this.escape(displayTitle));
        card.setAttribute('data-status', editedItem.status || 'draft');
        card.setAttribute('data-platform', editedItem.platform || '');
        const titleEl = card.querySelector('.item-title');
        if (titleEl) {
          titleEl.textContent = displayTitle;
          titleEl.setAttribute('data-title', this.escape(displayTitle));
        }
        const statusEl = card.querySelector('.status-pill');
        if (statusEl) statusEl.textContent = editedItem.status || 'draft';
        const snippetEl = card.querySelector('.item-snippet');
        if (snippetEl) snippetEl.textContent = (editedItem.content || '').substring(0, 100) + '...';
      }
    }

    // Clean up guards
    window.removeEventListener("keydown", this._boundEscHandler);
    window.removeEventListener("beforeunload", this._boundBeforeUnload);

    // Return focus
    if (this.lastFocusedEl) this.lastFocusedEl.focus();
  }

  handleEscKey(e) {
    if (e.key === "Escape" && this.editOpen) {
      this.closeEditDrawer();
    }
  }

  handleBeforeUnload(e) {
    if (this.editOpen && this.editDirty) {
      e.preventDefault();
      e.returnValue = "";
    }
  }

  _updateCampaignPreview() {
    const previewCont = this.container.querySelector("#campaign-drawer-preview");
    if (previewCont && this.editDraft) {
      previewCont.innerHTML = renderPreview(this.editDraft.platform || 'facebook', this.editDraft.content || '', this.editDraft.title || '', window.__auth__?.name || 'Your Brand', this.editDraft.media);
    }
    updateCharCounter('camp-char-counter', (this.editDraft?.content || '').length);
  }

  checkDirty() {
    const d = this.editDraft;
    const o = this.editOriginal;

    const contentChanged = d.content !== o.content;
    const titleChanged = (d.title || "") !== (o.title || "");
    const contextChanged = (d.context || "") !== (o.context || "");
    const mediaChanged = JSON.stringify(d.media || []) !== JSON.stringify(o.media || []);
    const mediaUrlsChanged = JSON.stringify(d.mediaUrls || []) !== JSON.stringify(o.mediaUrls || []);

    // Date comparison (normalize to ISO strings for comparison)
    const ds = d.scheduled_for ? new Date(d.scheduled_for).toISOString() : null;
    const os = o.scheduled_for ? new Date(o.scheduled_for).toISOString() : null;
    const dateChanged = ds !== os;

    this.editDirty = contentChanged || titleChanged || contextChanged || mediaChanged || mediaUrlsChanged || dateChanged;

    const saveBtn = this.container.querySelector("#btn-edit-save");
    if (saveBtn) saveBtn.disabled = !this.editDirty || this.editSaving;
  }

  async saveEdit() {
    if (!this.editDirty || this.editSaving) return;

    const payload = {
      title: this.editDraft.title,
      content: this.editDraft.content,
      media: this.editDraft.media,
      scheduled_for: this.editDraft.scheduled_for, // ISO string or null
      status: this.editDraft.scheduled_for ? 'scheduled' : 'draft',
      platform: this.editDraft.platform
    };

    if (!payload.content) {
      this.editError = "Content cannot be empty.";
      // Surgical: show error banner without full re-render (avoids item re-sort)
      const drawerBody = this.container.querySelector('.drawer-editor');
      if (drawerBody) {
        let errorBanner = drawerBody.querySelector('.edit-error-banner');
        if (!errorBanner) {
          errorBanner = document.createElement('div');
          errorBanner.className = 'edit-error-banner';
          drawerBody.insertBefore(errorBanner, drawerBody.firstChild);
        }
        errorBanner.textContent = this.editError;
      }
      return;
    }

    this.editSaving = true;
    this.editError = null;

    // Surgical: disable save button and show saving state (no full re-render to avoid item re-sort)
    const saveBtn = this.container.querySelector("#btn-edit-save");
    const indicator = this.container.querySelector("#save-indicator");
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving...'; }
    if (indicator) indicator.classList.add("hide");

    // Optimistic Update (internal state)
    const oldItems = [...this.campaignItems];
    this.campaignItems = this.campaignItems.map(item =>
      String(item.id) === String(this.editItemId) ? { ...item, ...payload, media: payload.media || [] } : item
    );

    try {
      const result = await client.updateCampaignItem(this.currentCampaign.id, this.editItemId, payload);

      // Authoritative update
      this.campaignItems = this.campaignItems.map(item =>
        String(item.id) === String(this.editItemId) ? result.data.item : item
      );

      this.editSaving = false;
      this.editDirty = false;
      this.editOriginal = { ...result.data.item };
      this.editDraft = { ...result.data.item };

      // Surgical: update save button and indicator (no full re-render)
      if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Save Changes'; }
      if (indicator) {
        indicator.classList.remove("hide");
        setTimeout(() => { if (indicator) indicator.classList.add("hide"); }, 3000);
      }

      // Surgically update the item card in the grid (preserve DOM order, no re-sort)
      const card = this.container.querySelector(`[data-item-id="${this.editItemId}"]`);
      if (card) {
        const editedItem = this.campaignItems.find(i => String(i.id) === String(this.editItemId));
        if (editedItem) {
          const displayTitle = editedItem.title || this.getComputedTitle(editedItem);
          card.setAttribute('data-title', this.escape(displayTitle));
          card.setAttribute('data-status', editedItem.status || 'draft');
          card.setAttribute('data-platform', editedItem.platform || '');
          const titleEl = card.querySelector('.item-title');
          if (titleEl) {
            titleEl.textContent = displayTitle;
            titleEl.setAttribute('data-title', this.escape(displayTitle));
          }
          const statusEl = card.querySelector('.status-pill');
          if (statusEl) statusEl.textContent = editedItem.status || 'draft';
          const snippetEl = card.querySelector('.item-snippet');
          if (snippetEl) snippetEl.textContent = (editedItem.content || '').substring(0, 100) + '...';
        }
      }

      // Surgically show inline toast (no full re-render)
      this.toast = { type: 'success', message: 'Saved ✓' };
      const toastContainer = this.container.querySelector('.toast-container');
      if (toastContainer) {
        toastContainer.innerHTML = `<div class="toast toast-success" data-testid="toast"><span class="toast-message">Saved ✓</span></div>`;
      }
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.clearToast(), 2500);
      // Clear dirty so the Close button can dismiss without the
      // "Discard Changes?" confirm. Leave the drawer open for the user —
      // auto-closing raced with user clicks and made the Close button
      // hang mid-animation under Playwright.
      this.editDirty = false;
    } catch (err) {
      // Rollback
      this.campaignItems = oldItems;
      this.editSaving = false;
      this.editError = `Save failed: ${err.message} (RID: ${err.requestId || 'N/A'})`;
      if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save Changes'; }
      toast("error", `Save failed: ${err.message}`);
      this.render(); // Full render on error is OK (recovery path)
    }
  }

  showToast(type, message) {
    this.clearToast();
    this.toast = { type, message };
    this.render();

    const duration = type === "success" ? 2500 : 4000;
    this.toastTimer = setTimeout(() => this.clearToast(), duration);
  }

  clearToast() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toast = null;
    this.toastTimer = null;
    // We only re-render if we are in detail mode to show the removal
    if (this.mode === "detail") this.render();
  }

  getFilteredItems() {
    let items = [...this.campaignItems];

    // Search
    if (this.view.search) {
      const s = this.view.search;
      items = items.filter(i =>
        (i.content || "").toLowerCase().includes(s) ||
        (i.title && i.title.toLowerCase().includes(s)) ||
        (i.context && i.context.toLowerCase().includes(s)) ||
        (i.platform || "").toLowerCase().includes(s) ||
        (i.status || "").toLowerCase().includes(s)
      );
    }

    // Filters
    if (this.view.filterPlatform !== "all") {
      items = items.filter(i => i.platform === this.view.filterPlatform);
    }
    if (this.view.filterStatus !== "all") {
      items = items.filter(i => i.status === this.view.filterStatus);
    }

    // Sort (stable: items without the sort key preserve original order)
    items.sort((a, b) => {
      switch (this.view.sort) {
        case "scheduled_asc":
          if (!a.scheduled_for && !b.scheduled_for) return 0;
          if (!a.scheduled_for) return 1;
          if (!b.scheduled_for) return -1;
          return new Date(a.scheduled_for) - new Date(b.scheduled_for);
        case "scheduled_desc":
          if (!a.scheduled_for && !b.scheduled_for) return 0;
          if (!a.scheduled_for) return 1;
          if (!b.scheduled_for) return -1;
          return new Date(b.scheduled_for) - new Date(a.scheduled_for);
        case "platform_asc":
          return (a.platform || '').localeCompare(b.platform || '');
        case "status_asc":
          return (a.status || '').localeCompare(b.status || '');
        case "title_asc":
          const ta = a.title || this.getComputedTitle(a);
          const tb = b.title || this.getComputedTitle(b);
          return ta.localeCompare(tb);
        default:
          return 0;
      }
    });

    return items;
  }

  async runBulkAction(payloadTemplate) {
    if (this.bulkRunning || this.selectedIds.size === 0) return;

    const ids = Array.from(this.selectedIds);
    this.bulkTotal = ids.length;
    this.bulkProgress = 0;
    this.bulkRunning = true;
    this.render();

    let successCount = 0;
    let failCount = 0;
    const failedIds = [];

    for (const id of ids) {
      try {
        const item = this.campaignItems.find(i => String(i.id) === String(id));
        if (!item) continue;

        let payload = { ...payloadTemplate };

        // Handle schedule logic if shifting
        if (payloadTemplate.shiftDays) {
          const current = item.scheduled_for ? new Date(item.scheduled_for) : new Date();
          current.setDate(current.getDate() + payloadTemplate.shiftDays);
          payload = { scheduled_for: current.toISOString() };
        }

        const result = await client.updateCampaignItem(this.currentCampaign.id, id, payload);

        // Update local state
        this.campaignItems = this.campaignItems.map(i => String(i.id) === String(id) ? result.data.item : i);
        this.selectedIds.delete(id);
        successCount++;
      } catch (err) {
        console.error(`Bulk update failed for ${id}: `, err);
        failCount++;
        failedIds.push(id);
      } finally {
        this.bulkProgress++;
        this.render();
      }
    }

    this.bulkRunning = false;
    this.bulkProgress = 0;
    this.bulkTotal = 0;

    if (failCount > 0) {
      this.showToast("error", `${successCount} updated, ${failCount} failed.Failed items remain selected.`);
    } else {
      this.showToast("success", `${successCount} items updated successfully.`);
      this.view.selectMode = false;
      this.saveViewSettings();
    }

    this.render();
  }

  // BF-7: Bulk delete — soft-deletes selected campaign items
  async runBulkDelete() {
    if (this.bulkRunning || this.selectedIds.size === 0) return;

    const ids = Array.from(this.selectedIds);
    this.bulkTotal = ids.length;
    this.bulkProgress = 0;
    this.bulkRunning = true;
    this.render();

    let successCount = 0;
    let failCount = 0;
    const failedIds = [];

    for (const id of ids) {
      try {
        await client.deleteFromLibrary(id);
        this.campaignItems = this.campaignItems.filter(i => String(i.id) !== String(id));
        this.selectedIds.delete(id);
        successCount++;
      } catch (err) {
        console.error(`Bulk delete failed for ${id}:`, err);
        failCount++;
        failedIds.push(id);
      } finally {
        this.bulkProgress++;
        this.render();
      }
    }

    this.bulkRunning = false;
    this.bulkProgress = 0;
    this.bulkTotal = 0;

    if (failCount > 0) {
      this.showToast("error", `${successCount} deleted, ${failCount} failed.`);
    } else {
      this.showToast("success", `${successCount} items deleted.`);
      this.view.selectMode = false;
      this.saveViewSettings();
    }

    this.render();
  }

  getComputedTitle(item) {
    if (!item) return 'Post';
    if (!item.content) return `${(item.platform || 'social').toUpperCase()} Post`;

    let text = item.content;
    // 1) Remove leading [STUB]
    text = text.replace(/^\[STUB\]\s*/i, '');

    // 2) Take first non-empty line
    const firstLine = text.split('\n').map(l => l.trim()).find(l => l.length > 0) || "";

    // 3) Strip URLs
    text = firstLine.replace(/https?:\/\/[^\s]+/g, '');

    // 4) Remove trailing hashtags chunk
    text = text.replace(/(\s*#[a-zA-Z0-9_]+\s*)+$/g, '').trim();

    // 5) Cut at first sentence punctuation if length > 40
    if (text.length > 40) {
      const match = text.match(/[.!?]/);
      if (match && match.index > 5) {
        text = text.substring(0, match.index);
      }
    }

    // 6) Collapse multiple spaces
    text = text.replace(/\s+/g, ' ').trim();

    // 7) Limit to 60 for suggestion
    if (text.length > 60) {
      text = text.substring(0, 57) + "...";
    }

    // 8) Fallback if too short
    if (text.length < 6) {
      const dateStr = new Date().toISOString().split('T')[0];
      return `${item.platform?.toUpperCase() || 'Social'} Post — ${dateStr} `;
    }

    return text;
  }

  renderCadenceSummary(campaign) {
    let rules = [];
    try {
      const raw = campaign.cadence_rules;
      if (raw) {
        rules = typeof raw === 'string' ? JSON.parse(raw) : raw;
      }
    } catch (e) {
      rules = [];
    }
    if (!Array.isArray(rules) || rules.length === 0) {
      return `<p style="font-size:13px;color:var(--text-muted);">
        <strong>Cadence:</strong> Daily (no specific days configured)
      </p>`;
    }
    const days = [...new Set(rules.map(r => r.day))].join(', ');
    const time = rules[0]?.time || '10:00';
    const platforms = [...new Set(rules.map(r => r.platform))].join(', ');
    return `<p style="font-size:13px;color:var(--text-muted);" data-testid="cadence-summary">
      <strong>Cadence:</strong> ${this.escape(days)} at ${this.escape(time)} &mdash; platforms: ${this.escape(platforms)}
    </p>`;
  }

  escape(str) {
    return escapeHtml(str);
  }

  async handleRewriteCampaign() {
    toast('info', 'Rewrite Campaign with Brain feature coming soon.');
  }

  mountFailedDrawer() {
    if (this.mode !== 'detail') return;
    const anchor = this.container?.querySelector('#campaign-failed-badge');
    if (!anchor) return;
    // Destroy previous drawer so it re-attaches to the freshly-rendered DOM anchor
    if (this._failedDrawer) {
      this._failedDrawer.destroy();
      this._failedDrawer = null;
    }
    this._failedDrawer = new FailedPostsDrawer({
      getBadgeAnchor: () => this.container?.querySelector('#campaign-failed-badge'),
      toast: (type, msg) => toast(type, msg),
      confirm: (opts) => confirmModal({
        ...opts,
        confirmLabel: 'Confirm',
        danger: false
      }),
      onRetry: async () => {},
      onCancel: async () => {},
    });
    this._failedDrawer.init();
  }

  unmount() {
    document.removeEventListener('keydown', this._boundEscHandler);
    window.removeEventListener('beforeunload', this._boundBeforeUnload);
    if (this.toastTimer) { clearTimeout(this.toastTimer); this.toastTimer = null; }
    if (this._failedDrawer) {
      this._failedDrawer.destroy();
      this._failedDrawer = null;
    }
  }
}
